import axios from 'axios';

/**
 * Retention service — fetches the tenant's effective retention (days
 * per data category) from the backend.
 *
 * Lives behind a session cache because:
 *   - the backend already serves a 5-minute Cache-Control,
 *   - and the picker is mounted on every dashboard page; we don't want
 *     a network round-trip on every navigation.
 *
 * Data shape (from /api/v1.0/retention/effective):
 *   {
 *     "days": { "rum": 7, "logs": 14, "traces": 14, "metrics": 30 },
 *     "max": 30,
 *     "tenantScoped": true
 *   }
 */

const STORAGE_KEY = 'verops_retention_effective_v1';
const TTL_MS = 5 * 60 * 1000;

const FALLBACK = {
  days: { rum: 30, logs: 30, traces: 30, metrics: 30 },
  max: 30,
  tenantScoped: false,
  // Marker: we used the offline default. The picker still works — it
  // just won't trim options based on the plan.
  fallback: true,
};

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expiresAt || parsed.expiresAt < Date.now()) return null;
    return parsed.payload;
  } catch (_) {
    return null;
  }
};

const writeCache = (payload) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      payload,
      expiresAt: Date.now() + TTL_MS,
    }));
  } catch (_) { /* private browsing / over quota — fine */ }
};

/** Public: get retention. Resolves quickly from cache when available. */
export const fetchRetention = async ({ force = false } = {}) => {
  if (!force) {
    const cached = readCache();
    if (cached) return cached;
  }
  try {
    const res = await axios.get('/api/v1.0/retention/effective', { timeout: 8000 });
    const payload = res?.data || FALLBACK;
    writeCache(payload);
    return payload;
  } catch (err) {
    // Don't surface as a user-facing error — the picker will fall
    // back to "show everything" rather than block UI.
    console.warn('[retention] fetchRetention failed:', err?.message || err);
    return FALLBACK;
  }
};

/** Force a refresh — used after a plan change applied via the operator panel. */
export const invalidateRetention = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
};

export const RETENTION_FALLBACK = FALLBACK;
